import { Component, Input, Output, EventEmitter, OnChanges } from '@angular/core';

import { BillFile, ParsedBillData } from '../../models';
import { formatBillDate } from '../../utils/date-format.util';
import { genericUanLabel, getBillAccountLabel } from '../../utils/bill-utils';

@Component({
  selector: 'app-bill-comparison',
  standalone: true,
  imports: [],
  templateUrl: './bill-comparison.component.html',
  styleUrl: './bill-comparison.component.scss'
})
export class BillComparisonComponent implements OnChanges {
    @Input() bills!: [BillFile, BillFile];
    @Output() close = new EventEmitter<void>();

    get billA(): BillFile {
        return this.bills[0];
    }

    get billB(): BillFile {
        return this.bills[1];
    }

    get dataA(): ParsedBillData {
        return this.billA.data || {} as ParsedBillData;
    }

    get dataB(): ParsedBillData {
        return this.billB.data || {} as ParsedBillData;
    }

    // Pre-computed template values
    accountNumberDifferent = false;
    accountNumberLabel = genericUanLabel(1);
    secondaryAccountNumberLabel = genericUanLabel(2);
    totalAmountDifferent = false;
    totalAmountA = '-';
    totalAmountB = '-';
    supplyChargesA = '-';
    supplyChargesB = '-';
    distributionChargesA = '-';
    distributionChargesB = '-';
    confidenceA = '-';
    confidenceB = '-';
    startDateA = '-';
    startDateB = '-';
    endDateA = '-';
    endDateB = '-';

    ngOnChanges() {
        this.computeDisplayValues();
    }

    private computeDisplayValues(): void {
        const a = this.dataA;
        const b = this.dataB;
        this.accountNumberDifferent = String(a.utilityAccountNumber || '') !== String(b.utilityAccountNumber || '');
        // Prefer the utility-specific name from whichever bill exposes it; fall
        // back to the generic "UAN N" when neither bill has a label.
        const labelA = getBillAccountLabel(a, a.utilityAccountNumber, '');
        const labelB = getBillAccountLabel(b, b.utilityAccountNumber, '');
        this.accountNumberLabel = labelA || labelB || genericUanLabel(1);
        const secLabelA = getBillAccountLabel(a, a.utilityAccountNumber2, '');
        const secLabelB = getBillAccountLabel(b, b.utilityAccountNumber2, '');
        this.secondaryAccountNumberLabel = secLabelA || secLabelB || genericUanLabel(2);
        this.totalAmountDifferent = String(a.totalAmount || '') !== String(b.totalAmount || '');
        this.totalAmountA = this.fmtCurrency(a.totalAmount, a.currency);
        this.totalAmountB = this.fmtCurrency(b.totalAmount, b.currency);
        this.supplyChargesA = this.fmtCurrency(a.supplyCharges, a.currency);
        this.supplyChargesB = this.fmtCurrency(b.supplyCharges, b.currency);
        this.distributionChargesA = this.fmtCurrency(a.distributionCharges, a.currency);
        this.distributionChargesB = this.fmtCurrency(b.distributionCharges, b.currency);
        this.startDateA = a.billingPeriodStart ? formatBillDate(a.billingPeriodStart) : '-';
        this.startDateB = b.billingPeriodStart ? formatBillDate(b.billingPeriodStart) : '-';
        this.endDateA = a.billingPeriodEnd ? formatBillDate(a.billingPeriodEnd) : '-';
        this.endDateB = b.billingPeriodEnd ? formatBillDate(b.billingPeriodEnd) : '-';
        this.confidenceA = this.fmtPercent(a.confidence);
        this.confidenceB = this.fmtPercent(b.confidence);
    }

    private fmtCurrency(val: number | undefined, currency: string = 'USD'): string {
        if (val === undefined || val === null) return '-';
        return `${currency} ${val.toFixed(2)}`;
    }

    private fmtPercent(val: number | undefined): string {
        if (val === undefined || val === null) return '-';
        return `${(val * 100).toFixed(0)}%`;
    }
}
